import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Pager, usePaged } from '../components/Pager';
import { SortHeader } from '../components/SortHeader';
import { EmptyState } from '../components/EmptyState';
import { RefreshButton } from '../components/RefreshButton';
import { PropertyFilter, type FilterProperty } from '../components/PropertyFilter';
import { devicePeerFilter, parseTransportFilter, relayChipText, transportBadge, TRANSPORT_PARAM, deviceCountText } from '../lib/vpnTransport';
import { Tooltip } from '../components/Tooltip';
import { Icon } from '../components/Icon';
import { Drawer } from '../components/Drawer';
import { compareBy, useUrlSort } from '../lib/useUrlState';
import {
  ENROLLED_PARAM,
  ENROLLED_PRESETS,
  enrolledSince,
  parseEnrolled,
  parseList,
  REGION_PARAM,
  serializeList,
  skipDeviceQuery,
  toggleValue,
} from '../lib/peerFilters';
import { timeAgo } from '../lib/agentCommands';
import { formatDate, formatDateTime } from '../lib/format';

/**
 * Network → Peers. Everyone and everything holding WireGuard credentials.
 *
 * READ-ONLY: every affordance is a filter, a sort or a selection. Enrollment and
 * revocation live in `app/optiedge/`; this page never writes `mdm-devices`.
 *
 * Layout (Claude Design handoff, 2026-09): one tab per network.yaml subnet, a region
 * summary whose cards filter, and a detail rail for the selected peer. No online/offline
 * state on any tab: every row shows how long since the peer's last handshake. Person subnets are fetched whole and sorted/paged here;
 * the device fleet is paged on the server, so its table narrows by search instead.
 *
 * All view state lives in the URL, so "support peers in APAC" is a link.
 */

export const VPN_EMPLOYEE_PEERS = gql`
  query VpnEmployeePeers {
    vpnEmployeePeers {
      _id
      publicKey
      name
      ownerEmail
      vpnIp
      subnet
      region
      createdByRole
      createdAt
      lastHandshakeAt
      kind
    }
  }
`;

export const VPN_DEVICE_PEERS = gql`
  query VpnDevicePeers($filter: VpnDevicePeerFilterInput, $pagination: VpnPaginationInput) {
    vpnDevicePeers(filter: $filter, pagination: $pagination) {
      total
      items {
        _id
        publicKey
        name
        vpnIp
        subnet
        region
        createdByRole
        createdAt
        lastHandshakeAt
        deviceName
        accountId
        utilsVersion
        appVersion
        kind
        deviceModel
        deviceModelRaw
        serialNo
        transport
        relayRegion
        relaySince
      }
    }
  }
`;

/** Devices on the TCP relay right now (bounded, 30 s-cached SCAN server-side). */
export const VPN_RELAY_SUMMARY = gql`
  query VpnRelaySummary {
    vpnRelaySummary {
      count
      truncated
      checkedAt
    }
  }
`;

export const VPN_PEERS_CONTEXT = gql`
  query VpnPeersContext {
    vpnSubnets {
      name
      cidr
      type
      googleGroup
    }
    vpnDeviceRegionCounts {
      region
      total
    }
    vpnHandshakeMeta {
      source
      checkedAt
    }
  }
`;

export const VPN_DEVICE_PEER = gql`
  query VpnDevicePeer($uuid: String!) {
    vpnDevicePeer(uuid: $uuid) {
      _id
      createdByTokenId
      updatedAt
      securelinkStatus
      securelinkStatusUpdatedAt
      transport
      relayRegion
      relaySince
    }
  }
`;

const VPN_PEER_TIMELINE = gql`
  query VpnPeerTimeline($uuid: String!, $windowDays: Int) {
    vpnPeerTimeline(uuid: $uuid, windowDays: $windowDays) {
      auditAvailable
      summary {
        windowDays
        attempts
        established
        successPercent
      }
      events {
        ts
        kind
        actor
        title
        detail
        outcome
      }
    }
  }
`;

export interface Peer {
  _id: string;
  publicKey?: string | null;
  name: string;
  ownerEmail?: string | null;
  vpnIp: string;
  subnet: string;
  region: string;
  createdByRole: string;
  createdAt: string;
  lastHandshakeAt?: string | null;
  kind?: string | null;
  deviceName?: string | null;
  accountId?: string | null;
  /** SecureLink client build from `devices.utilsVersion` ("4.55"). Device peers only. */
  utilsVersion?: string | null;
  /** Player app build from `devices.localAppVersion` ("5.19.55"). Device peers only. */
  appVersion?: string | null;
  /** MDM-parity hardware label ("OptiStick Gen 3"). Device peers only; null when unknown. */
  deviceModel?: string | null;
  /** The raw `devices.model` behind the label ("optisigns_player"), shown on hover. */
  deviceModelRaw?: string | null;
  /** Trimmed `devices.serialNo`; null when absent or blank. Device peers only. */
  serialNo?: string | null;
  /** 'relay' | 'udp' | null — how the tunnel reaches its hub right now. Device rows only. */
  transport?: string | null;
  relayRegion?: string | null;
  relaySince?: string | null;
}

export interface Subnet {
  name: string;
  cidr: string;
  type: string;
  googleGroup?: string | null;
}

// ── Regions ─────────────────────────────────────────────────────────────────
// The operator reads three regions. Raw values may be the orchestrator's own
// (`US`/`EU`/`APAC`) or cloud-style (`us-east-1`); both roll up to the same group.
type RegionGroup = 'US' | 'EU' | 'APAC';
export const REGION_GROUPS: RegionGroup[] = ['US', 'EU', 'APAC'];

export function regionGroup(raw?: string | null): RegionGroup | null {
  const r = (raw ?? '').toLowerCase();
  if (r === 'us' || r.startsWith('us-')) return 'US';
  if (r === 'eu' || r.startsWith('eu-')) return 'EU';
  if (r === 'apac' || r.startsWith('ap-')) return 'APAC';
  return null;
}

/**
 * A region none of the three groups cover (the sandbox fleet reports `SANDBOX`) keeps
 * its raw name as its own bucket. Without it the cards silently drop those peers and no
 * longer add up to the tab's total.
 */
export function regionKey(raw?: string | null): string {
  return regionGroup(raw) ?? (raw || 'unknown');
}

/** US/EU/APAC always, plus any other region actually present. */
export function regionKeys(raws: string[]): string[] {
  const groups: string[] = REGION_GROUPS;
  const others = [...new Set(raws.map(regionKey))].filter((k) => !groups.includes(k)).sort();
  return [...groups, ...others];
}

/** Peer `name` is `"<owner-email>:<hostname>"` for staff peers; show the machine part. */
export function machineName(p: Peer): string {
  const i = p.name.indexOf(':');
  return i > 0 ? p.name.slice(i + 1) : p.name;
}

/** Sortable columns of the person table. Region sorts by group first so US rows sit together. */
export const SORT_PICK: Record<string, (p: Peer) => unknown> = {
  machine: machineName,
  ownerEmail: (p) => p.ownerEmail,
  vpnIp: (p) => p.vpnIp,
  // US → EU → APAC → anything else, as the cards read. A numeric prefix, not a sentinel
  // character: `~` collates BEFORE letters under localeCompare, which put SANDBOX first.
  region: (p) => {
    const g = regionGroup(p.region);
    return `${g ? REGION_GROUPS.indexOf(g) : REGION_GROUPS.length}|${p.region}`;
  },
  lastHandshakeAt: (p) => p.lastHandshakeAt,
  createdAt: (p) => p.createdAt,
};

/**
 * The tab the bare `/vpn/peers` URL lands on. It is the default, the fallback for an
 * unknown `?tab=` value and the one name stripped from the URL — the three must move
 * together or a tab switch writes a URL that reopens on a different tab.
 */
export const DEFAULT_TAB = 'devices';

export const PEOPLE_PER_PAGE = 20;
export const DEVICE_PAGE_SIZES = [25, 50, 100, 200];
export const SEARCH_DEBOUNCE_MS = 300;
export const CLOSED = 'none';

/**
 * Several URL keys change together (a tab switch clears region, page and
 * selection). One setSearchParams call per gesture — two chained setters each build
 * from the same `prev` and the second discards the first (see useUrlSort).
 */
export function useUrlPatch() {
  const [params, setParams] = useSearchParams();
  const patch = (changes: Record<string, string | number | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(changes)) {
          if (v === null || v === '') next.delete(k);
          else next.set(k, String(v));
        }
        return next;
      },
      { replace: true },
    );
  return [params, patch] as const;
}

export function VpnPeersPage() {
  const [params, patch] = useUrlPatch();
  const sort = useUrlSort('ownerEmail');

  const ctx = useQuery<{
    vpnSubnets: Subnet[];
    vpnDeviceRegionCounts: { region: string; total: number }[];
    vpnHandshakeMeta: { source: string; checkedAt: string };
  }>(VPN_PEERS_CONTEXT, { fetchPolicy: 'cache-and-network' });
  const people = useQuery<{ vpnEmployeePeers: Peer[] }>(VPN_EMPLOYEE_PEERS, {
    fetchPolicy: 'cache-and-network',
  });

  // THE LAST GOOD READ (owner decision e-22): Apollo drops `data` when a refetch
  // errors, and a failed load must not blank rows an operator is reading.
  const lastPeople = useRef<Peer[]>([]);
  if (people.data?.vpnEmployeePeers) lastPeople.current = people.data.vpnEmployeePeers;
  const allPeople = people.data?.vpnEmployeePeers ?? lastPeople.current;

  const regionCounts = ctx.data?.vpnDeviceRegionCounts ?? [];
  const deviceFleetTotal = regionCounts.reduce((a, r) => a + r.total, 0);

  // Tab order is the design's: devices, engineers, pm-ops, qa-testlab, reserved, support —
  // alphabetical by subnet name. network.yaml lists them differently (engineers first,
  // devices last), so sort rather than trust the yaml; a new subnet then lands in a
  // predictable place without a hardcoded list. Copy first: Apollo's arrays are frozen.
  // `devices` sorts first, so the DEFAULT_TAB is also the leftmost tab — nothing jumps
  // when the subnet list arrives.
  const subnets: Subnet[] = useMemo(() => {
    const fromYaml = ctx.data?.vpnSubnets ?? [];
    const names = [...new Set(allPeople.map((p) => p.subnet))];
    const list = fromYaml.length
      ? [...fromYaml]
      : [...names.map((name) => ({ name, cidr: '', type: 'person' })), { name: 'devices', cidr: '', type: 'device' }];
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [ctx.data, allPeople]);

  const tab = params.get('tab') || DEFAULT_TAB;
  const active = subnets.find((s) => s.name === tab) ?? subnets.find((s) => s.name === DEFAULT_TAB) ?? subnets[0];
  const isDevices = active?.type === 'device';
  const regions = parseList(params.get(REGION_PARAM));
  const sel = params.get('sel') ?? '';

  const switchTab = (name: string) =>
    patch({
      tab: name === DEFAULT_TAB ? null : name,
      [REGION_PARAM]: null,
      [ENROLLED_PARAM]: null,
      page: null,
      dpage: null,
      sel: null,
    });
  // A region card and the filter box both toggle ONE value in the same list.
  const toggleRegion = (g: string) =>
    patch({ [REGION_PARAM]: serializeList(toggleValue(regions, g)), page: null, dpage: null, sel: null });

  const meta = ctx.data?.vpnHandshakeMeta;

  return (
    <div className="page page--wide">
      <div className="row-between" style={{ alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ margin: '0 0 var(--space-1)' }}>Peers</h1>
          <p className="muted" style={{ margin: 0, maxWidth: '70ch' }}>
            Everyone and everything with WireGuard credentials. Read-only — enrollment happens
            through the OptiEdge agent.
          </p>
        </div>
        <span className="badge quiet" style={{ whiteSpace: 'nowrap', padding: '3px 10px' }}>
          Read live from wg-orchestrator — not a scan
        </span>
      </div>

      {meta?.source === 'mongo-frozen' && (
        <div className="notice-note" role="status" data-testid="handshake-source-note">
          Handshake source unavailable — last handshake times may be missing or out of date until
          Redis is reachable again. The peer list itself is current.
        </div>
      )}

      {(people.error || ctx.error) && (
        <div className="error" role="alert" data-testid="peers-error">
          Couldn&apos;t load the peers — the API didn&apos;t respond.{' '}
          {allPeople.length > 0 ? 'The rows below are the last successful read.' : ''} The VPN
          itself is unaffected; this view only reads. If it persists, ask in #devops-infra.
        </div>
      )}

      <div className="tabs" role="tablist">
        {subnets.map((s) => {
          const count =
            s.type === 'device' ? deviceFleetTotal : allPeople.filter((p) => p.subnet === s.name).length;
          return (
            <button
              key={s.name}
              type="button"
              role="tab"
              aria-selected={s.name === active?.name}
              className={s.name === active?.name ? 'active' : undefined}
              title={`${s.cidr} · ${s.type}`}
              onClick={() => switchTab(s.name)}
            >
              {s.name} <span className="nav-count">{count.toLocaleString()}</span>
            </button>
          );
        })}
      </div>

      {active && (
        <div className="row muted text-xs" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
          {active.cidr && <span className="font-mono">{active.cidr}</span>}
          {active.cidr && <span>·</span>}
          <span>{active.type} subnet</span>
          {active.googleGroup && (
            <>
              <span>·</span>
              <span>{active.googleGroup}</span>
            </>
          )}
        </div>
      )}

      {active &&
        (isDevices ? (
          <DevicesView
            subnet={active}
            regions={regions}
            sel={sel}
            regionCounts={regionCounts}
            regionCountsLoaded={Boolean(ctx.data?.vpnDeviceRegionCounts)}
            patch={patch}
            params={params}
            toggleRegion={toggleRegion}
            refetchContext={() => ctx.refetch().catch(() => undefined)}
          />
        ) : (
          <PeopleView
            subnet={active}
            rows={allPeople.filter((p) => p.subnet === active.name)}
            loading={people.loading && !people.data}
            regions={regions}
            sel={sel}
            sort={sort}
            patch={patch}
            params={params}
            toggleRegion={toggleRegion}
            subnets={subnets}
            refetch={() =>
              Promise.all([people.refetch(), ctx.refetch()]).catch(() => undefined)
            }
          />
        ))}
    </div>
  );
}

type Patch = ReturnType<typeof useUrlPatch>[1];

// ── Person subnets: full list in hand, client-side filter / sort / page ─────
function PeopleView({
  subnet,
  subnets,
  rows,
  loading,
  regions,
  sel,
  sort,
  patch,
  params,
  toggleRegion,
  refetch,
}: {
  subnet: Subnet;
  subnets: Subnet[];
  rows: Peer[];
  loading: boolean;
  regions: string[];
  sel: string;
  sort: ReturnType<typeof useUrlSort>;
  patch: Patch;
  params: URLSearchParams;
  toggleRegion: (g: string) => void;
  refetch: () => unknown;
}) {
  const q = params.get('q') ?? '';
  const needle = q.trim().toLowerCase();
  const page = Math.max(1, Number(params.get('page')) || 1);
  const enrolled = parseEnrolled(params.get(ENROLLED_PARAM));
  const since = enrolledSince(enrolled);

  const wrapped = rows.map((p) => ({ p }));
  const inRegion = regions.length ? wrapped.filter((x) => regions.includes(regionKey(x.p.region))) : wrapped;
  const inEnrolled = since ? inRegion.filter((x) => x.p.createdAt >= since) : inRegion;
  const visible = needle
    ? inEnrolled.filter(
        ({ p }) =>
          p.name.toLowerCase().includes(needle) ||
          (p.ownerEmail ?? '').toLowerCase().includes(needle) ||
          p.vpnIp.toLowerCase().includes(needle),
      )
    : inEnrolled;
  const sorted = compareBy(visible, (x) => SORT_PICK[sort.key]?.(x.p), sort.dir);
  const pg = usePaged(sorted, PEOPLE_PER_PAGE, { page, onPage: (n) => patch({ page: n === 1 ? null : n }) });

  const cards = regionKeys(rows.map((p) => p.region)).map((g) => ({
    g,
    count: rows.filter((p) => regionKey(p.region) === g).length,
    sub: 'peers',
  }));

  const selected =
    sel === CLOSED ? null : (sorted.find((x) => x.p._id === sel) ?? (sel ? null : pg.pageItems[0]))?.p ?? null;
  const properties: FilterProperty[] = [
    {
      key: REGION_PARAM,
      label: 'Region',
      options: cards.map((c) => ({ value: c.g, label: c.g, count: c.count.toLocaleString() })),
      selected: regions,
    },
    {
      key: ENROLLED_PARAM,
      label: 'Enrolled',
      options: ENROLLED_PRESETS.map((p) => ({ value: p.value, label: p.label })),
      selected: enrolled ? [enrolled] : [],
    },
  ];
  const onToggle = (key: string, v: string) => {
    if (key === REGION_PARAM) toggleRegion(v);
    // One range at a time: picking another preset replaces it, picking the same one clears it.
    else if (key === ENROLLED_PARAM) patch({ [ENROLLED_PARAM]: enrolled === v ? null : v, page: null, sel: null });
  };

  if (loading) return <SkeletonRows />;

  return (
    <>
      <RegionCard
        label={`${subnet.name} by region`}
        cards={cards}
        selected={regions}
        onToggle={toggleRegion}
        onClear={() => patch({ [REGION_PARAM]: null, page: null, sel: null })}
      />

      <PropertyFilter
        properties={properties}
        onToggle={onToggle}
        onClearProperty={(key) => patch({ [key]: null, page: null, sel: null })}
        text={q}
        onText={(t) => patch({ q: t, page: null, sel: null })}
        textHint="machine, owner, VPN IP"
        placeholder="Filter peers by property or search machine, owner, VPN IP"
        leading={<RefreshButton refetch={refetch} />}
        trailing={
          <span className="filter-count">
            {visible.length} of {rows.length}
          </span>
        }
        onClearAll={() => patch({ q: null, [REGION_PARAM]: null, [ENROLLED_PARAM]: null, page: null, sel: null })}
      />

      <TwoColumns
        rail={
          selected && (
            <PeerRail
              peer={selected}
              subnet={subnets.find((s) => s.name === selected.subnet) ?? subnet}
              onClose={() => patch({ sel: CLOSED })}
            />
          )
        }
      >
        {rows.length === 0 ? (
          <EmptyState
            icon="cluster"
            title={`No peers in ${subnet.name}`}
            body="Staff laptops appear here once they enroll via the OptiEdge agent."
          />
        ) : visible.length === 0 ? (
          <p className="muted">No peer matches these filters.</p>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="table-dense">
                <thead>
                  <tr>
                    <SortHeader label="Machine" sortKey="machine" sort={sort} />
                    <SortHeader label="Owner" sortKey="ownerEmail" sort={sort} />
                    <SortHeader label="VPN IP" sortKey="vpnIp" sort={sort} />
                    <SortHeader label="Region" sortKey="region" sort={sort} />
                    <SortHeader
                      label="Last handshake"
                      sortKey="lastHandshakeAt"
                      sort={sort}
                      title="Sort by when the peer last handshook"
                    />
                    <SortHeader label="Enrolled" sortKey="createdAt" sort={sort} />
                  </tr>
                </thead>
                <tbody>
                  {pg.pageItems.map(({ p }) => (
                    <PeerRow key={p._id} peer={p} selected={p._id === selected?._id} onSelect={() => patch({ sel: p._id })}>
                      <td title={p.name}>{machineName(p)}</td>
                      <td className="muted">{p.ownerEmail ?? '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <span className="ip-cell font-mono">{p.vpnIp}</span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <RegionBadge raw={p.region} />
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <HandshakeAge at={p.lastHandshakeAt} />
                      </td>
                      <td className="muted text-xs" style={{ whiteSpace: 'nowrap' }}>
                        {formatDate(p.createdAt)}
                      </td>
                    </PeerRow>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager
              page={pg.page}
              pageCount={pg.pageCount}
              total={pg.total}
              start={pg.start}
              pageSize={pg.pageSize}
              onPage={pg.setPage}
            />
          </>
        )}
      </TwoColumns>
    </>
  );
}

// ── Devices: paged on the server, narrowed by search + region ───────────────
function DevicesView({
  subnet,
  regions,
  sel,
  regionCounts,
  regionCountsLoaded,
  patch,
  params,
  toggleRegion,
  refetchContext,
}: {
  subnet: Subnet;
  regions: string[];
  sel: string;
  regionCounts: { region: string; total: number }[];
  /** False until `vpnDeviceRegionCounts` has answered once; a region filter cannot resolve before. */
  regionCountsLoaded: boolean;
  patch: Patch;
  params: URLSearchParams;
  toggleRegion: (g: string) => void;
  refetchContext: () => unknown;
}) {
  const dq = params.get('dq') ?? '';
  const dpage = Math.max(1, Number(params.get('dpage')) || 1);
  const dperRaw = Number(params.get('dper'));
  const dper = DEVICE_PAGE_SIZES.includes(dperRaw) ? dperRaw : DEVICE_PAGE_SIZES[0];
  const transport = parseTransportFilter(params.get(TRANSPORT_PARAM));
  const enrolled = parseEnrolled(params.get(ENROLLED_PARAM));
  const since = enrolledSince(enrolled);

  // The page's "US" is every raw region that rolls up to it.
  const rawRegions = regions.length
    ? regionCounts.filter((r) => regions.includes(regionKey(r.region))).map((r) => r.region)
    : [];
  const waitingForRegions = regions.length > 0 && !regionCountsLoaded;
  const offset = (dpage - 1) * dper;
  const { data, loading, error, refetch } = useQuery<{ vpnDevicePeers: { items: Peer[]; total: number } }>(
    VPN_DEVICE_PEERS,
    {
      variables: {
        filter: devicePeerFilter(dq, rawRegions, transport, since),
        pagination: { offset, limit: dper },
      },
      skip: skipDeviceQuery(regions, rawRegions, regionCountsLoaded),
      fetchPolicy: 'cache-and-network',
    },
  );

  // The "N on relay" chip loads on its own so a cold relay scan never holds up the table.
  // An error just hides the chip: the count is a convenience, the rows are the page.
  const relay = useQuery<{ vpnRelaySummary: { count: number | null; truncated: boolean } }>(
    VPN_RELAY_SUMMARY,
    { fetchPolicy: 'cache-and-network' },
  );
  const relayChip = relayChipText(relay.data?.vpnRelaySummary);
  const relayUnknown = relay.data ? relay.data.vpnRelaySummary.count === null : false;

  // THE LAST GOOD READ, as the people table has it (see VpnPeersPage): Apollo drops
  // `data` when a refetch errors, and a failed refresh must not blank the rows an
  // operator is reading. The error line goes above the table, not instead of it.
  // It applies ONLY on error: while a page or filter is loading the table must show its
  // loading row, and a skipped read (a region with no raw values) must show nothing —
  // keeping the old rows there would be a lie about what the filter matched.
  const lastDevices = useRef<{ items: Peer[]; total: number }>({ items: [], total: 0 });
  if (data?.vpnDevicePeers) lastDevices.current = data.vpnDevicePeers;
  const fallback = error ? lastDevices.current : { items: [], total: 0 };
  const items = data?.vpnDevicePeers.items ?? fallback.items;
  const total = data?.vpnDevicePeers.total ?? fallback.total;
  const pageCount = Math.max(1, Math.ceil(total / dper));
  const visible = items.map((p) => ({ p }));

  const cards = regionKeys(regionCounts.map((r) => r.region)).map((g) => ({
    g,
    count: regionCounts.filter((r) => regionKey(r.region) === g).reduce((a, r) => a + r.total, 0),
    sub: 'device peers',
  }));

  const selected =
    sel === CLOSED ? null : (visible.find((x) => x.p._id === sel) ?? (sel ? null : visible[0]))?.p ?? null;
  const anyFilter = Boolean(dq || regions.length || transport || enrolled);
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + items.length, total);

  return (
    <>
      <RegionCard
        label="Device fleet by region"
        cards={cards}
        selected={regions}
        onToggle={toggleRegion}
        onClear={() => patch({ [REGION_PARAM]: null, dpage: null, sel: null })}
      />

      <PropertyFilter
        properties={[
          {
            key: REGION_PARAM,
            label: 'Region',
            options: cards.map((c) => ({ value: c.g, label: c.g, count: c.count.toLocaleString() })),
            selected: regions,
          },
          {
            key: TRANSPORT_PARAM,
            label: 'Transport',
            // Only `relay` exists server-side (see vpnTransport.ts); "UDP" is not a bounded read.
            options: [{ value: 'relay', label: 'TCP relay', count: relayChip ?? undefined }],
            selected: transport ? [transport] : [],
          },
          {
            key: ENROLLED_PARAM,
            label: 'Enrolled',
            options: ENROLLED_PRESETS.map((p) => ({ value: p.value, label: p.label })),
            selected: enrolled ? [enrolled] : [],
          },
        ]}
        onToggle={(key, v) => {
          if (key === REGION_PARAM) toggleRegion(v);
          // Transport and Enrolled hold one value: the same pick clears it, another replaces it.
          else if (key === TRANSPORT_PARAM)
            patch({ [TRANSPORT_PARAM]: transport === v ? null : v, dpage: null, sel: null });
          else if (key === ENROLLED_PARAM)
            patch({ [ENROLLED_PARAM]: enrolled === v ? null : v, dpage: null, sel: null });
        }}
        onClearProperty={(key) => patch({ [key]: null, dpage: null, sel: null })}
        text={dq}
        onText={(t) => patch({ dq: t, dpage: null, sel: null })}
        textHint="UUID, serial, VPN IP"
        placeholder="Filter devices by property or search UUID, serial, VPN IP"
        leading={
          <RefreshButton
            refetch={() =>
              Promise.all([refetch(), refetchContext(), relay.refetch()]).catch(() => undefined)
            }
          />
        }
        trailing={
          <>
            {relayChip && (
              <button
                type="button"
                className="badge health-amber"
                aria-pressed={transport === 'relay'}
                title="Devices with a live wg-relay session right now (refreshed about every 30 s). Click to list them."
                data-testid="relay-count-chip"
                onClick={() => patch({ [TRANSPORT_PARAM]: transport === 'relay' ? null : 'relay', dpage: null, sel: null })}
                style={{ cursor: 'pointer', fontFamily: 'inherit' }}
              >
                {relayChip}
              </button>
            )}
            <span className="filter-count">{deviceCountText(total)}</span>
          </>
        }
        onClearAll={() =>
          patch({ dq: null, [REGION_PARAM]: null, [TRANSPORT_PARAM]: null, [ENROLLED_PARAM]: null, dpage: null, sel: null })
        }
      />

      <TwoColumns
        rail={
          selected && <PeerRail peer={selected} subnet={subnet} onClose={() => patch({ sel: CLOSED })} />
        }
      >
        {transport === 'relay' && relayUnknown && (
          <div className="notice-note" role="status" data-testid="relay-unknown-note">
            The relay list is unavailable right now (Redis unreachable), so this filter shows no
            devices. It does not mean nobody is on the relay.
          </div>
        )}
        {error && (
          <div className="error" role="alert" data-testid="device-peers-error">
            Couldn&apos;t load the device peers.{' '}
            {items.length > 0 ? 'The rows below are the last successful read.' : ''} The person
            subnets come from the same source, so if they loaded this is likely the fleet query
            timing out. Retry, then ask in #devops-infra.
          </div>
        )}
        <div style={{ overflowX: 'auto' }}>
          <table className="table-dense">
            <thead>
              <tr>
                {/* No SortHeader: the server returns one page at a time, so a sort
                    here would only reorder the rows in hand. */}
                <th>Device</th>
                <th>Model</th>
                <th>VPN IP</th>
                <th>Region</th>
                <th>Last handshake</th>
                <th>Transport</th>
                <th>Enrolled</th>
              </tr>
            </thead>
            <tbody>
              {(loading || waitingForRegions) && items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="muted">
                    Loading devices…
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={7} className="muted">
                    {anyFilter ? 'No devices match these filters.' : 'No device peers yet.'}
                  </td>
                </tr>
              ) : (
                visible.map(({ p }) => (
                  <PeerRow key={p._id} peer={p} selected={p._id === selected?._id} onSelect={() => patch({ sel: p._id })}>
                    <td>
                      <div>{p.deviceName || <span className="muted">(unnamed)</span>}</div>
                      <div className="muted text-2xs font-mono">{p.name}</div>
                    </td>
                    <DeviceModelCell peer={p} />
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span className="ip-cell font-mono">{p.vpnIp}</span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <RegionBadge raw={p.region} />
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <HandshakeAge at={p.lastHandshakeAt} />
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }} data-testid="transport-cell">
                      <TransportCell peer={p} />
                    </td>
                    <td className="muted text-xs" style={{ whiteSpace: 'nowrap' }}>
                      {formatDate(p.createdAt)}
                    </td>
                  </PeerRow>
                ))
              )}
            </tbody>
          </table>
        </div>
        <p className="muted text-xs" style={{ margin: 'var(--space-2) 0 0' }}>
          The fleet is paged server-side, so this table narrows by search rather than sorting — a
          sort would only reorder the rows in hand.
        </p>
        {total > 0 && (
          <div className="pager">
            <span className="muted">
              Showing {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}
            </span>
            <div className="pager-btns">
              <select
                value={dper}
                onChange={(e) => patch({ dper: Number(e.target.value) === DEVICE_PAGE_SIZES[0] ? null : e.target.value, dpage: null, sel: null })}
                title="devices per page"
                className="class-select class-select--sm"
                style={{ minWidth: '7.5rem' }}
              >
                {DEVICE_PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n} per page
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="mini-btn"
                disabled={dpage <= 1 || loading}
                onClick={() => patch({ dpage: dpage - 1 === 1 ? null : dpage - 1, sel: null })}
              >
                ‹ Prev
              </button>
              <span className="muted">
                Page {dpage} / {pageCount.toLocaleString()}
              </span>
              <button
                type="button"
                className="mini-btn"
                disabled={dpage >= pageCount || loading}
                onClick={() => patch({ dpage: dpage + 1, sel: null })}
              >
                Next ›
              </button>
            </div>
          </div>
        )}
      </TwoColumns>
    </>
  );
}

// ── Shared pieces ───────────────────────────────────────────────────────────

function TwoColumns({ children, rail }: { children: ReactNode; rail?: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 520px', minWidth: 0 }}>{children}</div>
      {rail}
    </div>
  );
}

function RegionCard({
  label,
  cards,
  selected,
  onToggle,
  onClear,
}: {
  label: string;
  cards: { g: string; count: number; sub: string }[];
  selected: string[];
  onToggle: (g: string) => void;
  onClear: () => void;
}) {
  return (
    <section className="card" style={{ padding: 'var(--space-3) var(--space-4)' }}>
      <div className="row-between" style={{ alignItems: 'baseline', marginBottom: 'var(--space-3)' }}>
        <div className="kv-k">{label}</div>
        <div className="muted text-xs">Click a region to filter</div>
      </div>
      <div className="stat-row" style={{ margin: 0 }}>
        {cards.map((c) => {
          const on = selected.includes(c.g);
          return (
            <button
              key={c.g}
              type="button"
              className="stat"
              aria-pressed={on}
              onClick={() => onToggle(c.g)}
              style={{
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'inherit',
                background: on ? 'var(--accent-soft)' : undefined,
                borderColor: on ? 'var(--accent)' : undefined,
              }}
            >
              <div className="label">{c.g}</div>
              <div className="value">{c.count.toLocaleString()}</div>
              <div className="muted text-xs">{c.sub}</div>
            </button>
          );
        })}
      </div>
      {selected.length > 0 && (
        <button
          type="button"
          className="mini-btn"
          onClick={onClear}
          style={{ marginTop: 'var(--space-3)', borderStyle: 'dashed', color: 'var(--muted)' }}
        >
          All regions
        </button>
      )}
    </section>
  );
}

/** A clickable, keyboard-reachable row. The selected row carries the accent bar. */
function PeerRow({
  peer,
  selected,
  onSelect,
  children,
}: {
  peer: Peer;
  selected: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  const onKey = (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  };
  return (
    <tr
      className="peer-row"
      tabIndex={0}
      aria-selected={selected}
      aria-label={`Show details for ${peer.deviceName || peer.name}`}
      onClick={onSelect}
      onKeyDown={onKey}
      style={selected ? { background: 'var(--accent-soft)', boxShadow: 'inset 2px 0 0 var(--accent)' } : undefined}
    >
      {children}
    </tr>
  );
}

/**
 * The MDM-parity hardware label ("OptiStick Gen 3"), with the raw `devices.model` on hover.
 * `—` when the API has no `devices` doc for that UUID — the glyph is the frontend's, the API
 * only ever sends null. No `title` attribute at all when the raw model is null, so a hover
 * never shows the string "null". Device peers only: people rows carry no Model column.
 */
function DeviceModelCell({ peer }: { peer: Peer }) {
  return (
    <td
      style={{ whiteSpace: 'nowrap' }}
      title={peer.deviceModelRaw ?? undefined}
      data-testid="device-model-cell"
    >
      {peer.deviceModel ?? '—'}
    </td>
  );
}

/** The Transport cell: the rail's badge, or a dash when the server did not say. */
function TransportCell({ peer }: { peer: Peer }) {
  const b = transportBadge(peer);
  if (!b) return <span className="muted">—</span>;
  return (
    <span className={`badge ${b.className}`} title={b.title}>
      {b.label}
    </span>
  );
}

function RegionBadge({ raw }: { raw: string }) {
  return (
    <span className="badge" title={raw}>
      {regionGroup(raw) ?? raw}
    </span>
  );
}

/**
 * A peer's handshake state, in three buckets rather than two.
 *
 * wg-orchestrator re-publishes `wghs:<publicKey>` every 2 minutes for every peer
 * whose KERNEL handshake time is non-zero, refreshing the TTL each pass
 * (`handshake_sync.go`: "Peers with a zero handshake are skipped"). So the key
 * does not age out when a device goes quiet — it is still republished carrying a
 * frozen value. That makes absence and staleness mean different things:
 *
 *   no value      → the kernel never saw a handshake: NEVER connected
 *   fresh value   → connected, rekeying (WireGuard rekeys ~2 min under traffic)
 *   stale value   → it did connect once, and has been silent since
 *
 * The page used to print "never" for the first case and a bare age for the other
 * two, collapsing "never worked" into the same word as "worked, then stopped" —
 * measured on production at 4–30% of peers depending on age, so the distinction
 * is not a corner case.
 *
 * `QUIET_AFTER_MS` is a display threshold, not a verdict: an idle-but-healthy
 * tunnel does not rekey either, so a stale value means "no traffic since", which
 * is why the label says that rather than "offline".
 */
const QUIET_AFTER_MS = 10 * 60 * 1000;

export function handshakeState(at?: string | null): 'never' | 'live' | 'quiet' {
  if (!at) return 'never';
  return Date.now() - new Date(at).getTime() <= QUIET_AFTER_MS ? 'live' : 'quiet';
}

function HandshakeAge({ at }: { at?: string | null }) {
  const state = handshakeState(at);
  if (state === 'never') {
    return (
      <span
        className="muted text-xs"
        title="The gateway has never recorded a handshake for this peer — it has not connected once."
      >
        never connected
      </span>
    );
  }
  return (
    <span
      className={state === 'quiet' ? 'text-xs' : 'muted text-xs'}
      style={state === 'quiet' ? { color: 'var(--amber)' } : undefined}
      title={
        state === 'quiet'
          ? `Connected before; no handshake since ${formatDateTime(at!)}`
          : formatDateTime(at!)
      }
    >
      {timeAgo(at!)}
    </span>
  );
}

function SkeletonRows() {
  return (
    <div className="skeleton-row-group">
      {[0, 1, 2, 3, 4].map((i) => (
        <div className="skeleton-row" key={i} />
      ))}
    </div>
  );
}

/** A `?` beside a card title whose explanation shows on hover AND keyboard focus. */
function Explain({ label, text }: { label: string; text: string }) {
  return (
    <Tooltip text={text}>
      {(id) => (
        <button
          type="button"
          aria-label={label}
          aria-describedby={id}
          style={{
            background: 'none',
            border: 0,
            padding: 0,
            display: 'inline-flex',
            color: 'var(--muted)',
            cursor: 'help',
          }}
        >
          <Icon name="question" size={14} />
        </button>
      )}
    </Tooltip>
  );
}

function RailRow({ k, children }: { k: string; children: ReactNode }) {
  return (
    <div className="row-between text-sm" style={{ gap: 'var(--space-2)' }}>
      <span className="muted">{k}</span>
      <span style={{ textAlign: 'right', overflowWrap: 'anywhere', minWidth: 0 }}>{children}</span>
    </div>
  );
}

function PeerRail({ peer, subnet, onClose }: { peer: Peer; subnet: Subnet; onClose: () => void }) {
  const isDevice = peer.kind === 'device';
  const [showKey, setShowKey] = useState(false);
  useEffect(() => setShowKey(false), [peer._id]);

  // Token id + record-updated exist only on the raw device record — fetched on selection.
  const raw = useQuery<{
    vpnDevicePeer: {
      createdByTokenId?: string | null;
      updatedAt: string;
      securelinkStatus?: string | null;
      securelinkStatusUpdatedAt?: string | null;
      transport?: string | null;
      relayRegion?: string | null;
      relaySince?: string | null;
    } | null;
  }>(
    VPN_DEVICE_PEER,
    { variables: { uuid: peer.name }, skip: !isDevice },
  );
  const rawPeer = raw.data?.vpnDevicePeer;

  return (
    <aside
      className="detail-rail stack-sm"
      aria-label="Peer detail"
      style={{ flex: '1 1 320px', maxWidth: 360, position: 'sticky', top: 80 }}
    >
      <section className="rail-card">
        <div className="row-between" style={{ alignItems: 'flex-start' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600, overflowWrap: 'anywhere' }}>
              {isDevice ? peer.deviceName || '(unnamed device)' : machineName(peer)}
            </div>
            <div className="muted text-sm" style={{ overflowWrap: 'anywhere' }}>
              {isDevice ? peer.accountId ?? '—' : peer.ownerEmail ?? '—'}
            </div>
          </div>
          <button type="button" className="mini-btn" onClick={onClose} aria-label="Close peer detail" style={{ margin: 0, padding: '0 8px' }}>
            <Icon name="close" size={12} />
          </button>
        </div>
        <div className="row" style={{ marginTop: 'var(--space-3)', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <span className={`badge ${handshakeBadgeClass(peer.lastHandshakeAt)}`}>
            {handshakeState(peer.lastHandshakeAt) === 'never'
              ? 'Never connected'
              : `Last handshake · ${timeAgo(peer.lastHandshakeAt!)}`}
          </span>
          {isDevice && rawPeer?.securelinkStatus && (
            <span
              className={`badge ${statusBadgeClass(rawPeer.securelinkStatus, peer.lastHandshakeAt)}`}
              title={statusBadgeTitle(rawPeer.securelinkStatus, rawPeer.securelinkStatusUpdatedAt, peer.lastHandshakeAt)}
            >
              {rawPeer.securelinkStatus}
            </span>
          )}
          {isDevice && transportBadge(rawPeer) && (
            <span
              className={`badge ${transportBadge(rawPeer)!.className}`}
              title={transportBadge(rawPeer)!.title}
              data-testid="rail-transport"
            >
              {transportBadge(rawPeer)!.label}
            </span>
          )}
        </div>
      </section>

      <section className="rail-card">
        <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
          <h3 className="rail-card__title" style={{ margin: 0 }}>
            Network
          </h3>
          <Explain
            label="About last handshake"
            text="Read from Redis, where wg-orchestrator publishes it about every 2 minutes, so it can be up to ~2 min old."
          />
        </div>
        <div className="stack-sm" style={{ marginTop: 'var(--space-3)' }}>
          <RailRow k="Subnet">
            <Link to="/vpn/subnets">{subnet.name}</Link>{' '}
            {subnet.cidr && <span className="muted font-mono text-xs">{subnet.cidr}</span>}
          </RailRow>
          <RailRow k="Region">
            <RegionBadge raw={peer.region} />
          </RailRow>
          <RailRow k="VPN IP">
            <span className="font-mono text-xs">{peer.vpnIp}</span>
          </RailRow>
          <RailRow k="Last handshake">
            {peer.lastHandshakeAt ? (
              formatDateTime(peer.lastHandshakeAt)
            ) : (
              <span title="The gateway has never recorded a handshake for this peer.">
                never connected
              </span>
            )}
          </RailRow>
        </div>
      </section>

      <section className="rail-card">
        <h3 className="rail-card__title">Identity</h3>
        <div className="stack-sm">
          <RailRow k="Kind">
            <span className="badge">{peer.kind ?? (isDevice ? 'device' : 'employee')}</span>
          </RailRow>
          <RailRow k="Peer name">
            <span className="font-mono text-2xs">{peer.name}</span>
          </RailRow>
          {isDevice ? (
            <>
              <RailRow k="Device name">{peer.deviceName ?? '—'}</RailRow>
              {/* Same two values as the row's Model cell — the list item already holds them,
                  and `vpnDevicePeer(uuid:)` has no `devices` join to fetch them from. */}
              <RailRow k="Model">
                <span title={peer.deviceModelRaw ?? undefined} data-testid="rail-device-model">
                  {peer.deviceModel ?? '—'}
                </span>
              </RailRow>
              <RailRow k="Serial number">
                <span className="font-mono" data-testid="rail-serial-no">{peer.serialNo ?? '—'}</span>
              </RailRow>
              <RailRow k="Utils version">
                <span className="font-mono" data-testid="rail-utils-version">{peer.utilsVersion ?? '—'}</span>
              </RailRow>
              <RailRow k="App version">
                <span className="font-mono" data-testid="rail-app-version">{peer.appVersion ?? '—'}</span>
              </RailRow>
              <RailRow k="Account">{peer.accountId ?? '—'}</RailRow>
            </>
          ) : (
            <RailRow k="Owner">{peer.ownerEmail ?? '—'}</RailRow>
          )}
          <div className="stack-sm" style={{ gap: 'var(--space-1)' }}>
            <div className="row-between text-sm">
              <span className="muted">Public key</span>
              <span className="row" style={{ gap: 'var(--space-2)' }}>
                <span className="badge quiet">staff-only</span>
                {peer.publicKey && (
                  <button type="button" className="mini-btn" onClick={() => setShowKey((v) => !v)} style={{ margin: 0 }}>
                    {showKey ? 'Hide' : 'Show'}
                  </button>
                )}
              </span>
            </div>
            <div
              className="font-mono text-2xs"
              style={{
                overflowWrap: 'anywhere',
                padding: '6px 8px',
                background: 'var(--panel-2)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              {!peer.publicKey ? '—' : showKey ? peer.publicKey : `${peer.publicKey.slice(0, 6)}${'•'.repeat(18)}`}
            </div>
          </div>
        </div>
      </section>

      <section className="rail-card">
        <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
          <h3 className="rail-card__title" style={{ margin: 0 }}>
            Enrollment
          </h3>
          <Explain
            label="About enrollment"
            text="Read-only view of mdm-devices, owned by wg-orchestrator. Enrollment and revocation happen elsewhere; this page never writes these records."
          />
        </div>
        <div className="stack-sm" style={{ marginTop: 'var(--space-3)' }}>
          <RailRow k="Created">{formatDateTime(peer.createdAt)}</RailRow>
          <RailRow k="Created by role">
            <span className="badge">{peer.createdByRole}</span>
          </RailRow>
          {isDevice && (
            <>
              <RailRow k="Token id">
                <span className="font-mono text-2xs">{raw.loading ? '…' : rawPeer?.createdByTokenId ?? '—'}</span>
              </RailRow>
              <RailRow k="Record updated">{raw.loading ? '…' : rawPeer ? formatDateTime(rawPeer.updatedAt) : '—'}</RailRow>
            </>
          )}
        </div>
      </section>

      {isDevice && <TimelineCard uuid={peer.name} />}
    </aside>
  );
}

/**
 * A device peer's timeline: when SecureLink was allocated, what status the
 * device last reported, and every staff reach attempt in the window.
 *
 * Rows arrive from the server already normalised and formatted, so this
 * component renders `outcome` → colour and nothing else. When the provisioning
 * lifecycle (`securelink_events`) lands, it becomes more rows of the same
 * shape — no change here.
 */
const TIMELINE_PREVIEW_ROWS = 10;

/** The rail is a glance: the newest few. The full record lives in the drawer. */
const TIMELINE_WINDOW_DAYS = 30;

export function TimelineCard({ uuid }: { uuid: string }) {
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [uuid]);
  const { data, loading } = useQuery<{ vpnPeerTimeline: Timeline | null }>(VPN_PEER_TIMELINE, {
    variables: { uuid, windowDays: TIMELINE_WINDOW_DAYS },
  });
  const t = data?.vpnPeerTimeline;
  const s = t?.summary;
  const preview = previewEvents(t?.events);

  return (
    <section className="rail-card">
      <div className="row-between" style={{ alignItems: 'center', gap: 'var(--space-2)' }}>
        <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
          <h3 className="rail-card__title" style={{ margin: 0 }}>
            Timeline
          </h3>
          <Explain
            label="About the timeline"
            text="Allocation and status come from the peer and device records; the reach attempts come from the wg-orchestrator connection audit. Status is the device's latest report, not a history — it has no record of earlier changes."
          />
        </div>
      </div>

      {s && s.attempts > 0 && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <span className={`badge ${reachBadgeClass(s.successPercent)}`}>
            Reached {s.established}/{s.attempts} · {s.successPercent}%
          </span>
        </div>
      )}
      {/* Only when the audit source is actually wired. Without it, zero attempts
          means "not recorded here", not "nobody tried" — the footer note below
          says which, and claiming the latter would be a false negative. */}
      {s && s.attempts === 0 && t?.auditAvailable && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <span className="badge quiet">No one tried to reach it in {TIMELINE_WINDOW_DAYS} days</span>
        </div>
      )}

      <div className="stack-sm" style={{ marginTop: 'var(--space-3)' }}>
        {loading && <span className="muted text-sm">…</span>}
        {!loading && t && t.events.length === 0 && (
          <span className="muted text-sm">Nothing recorded in this window.</span>
        )}
        {!loading && preview.map((e, i) => <TimelineRow key={`${e.ts}-${i}`} e={e} />)}
      </div>

      {!loading && t && t.events.length > preview.length && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(true)}>
            Read more · {t.events.length} events
          </button>
        </div>
      )}

      {t && !t.auditAvailable && (
        <div className="muted text-2xs" style={{ marginTop: 'var(--space-3)' }}>
          Connection audit is not configured here — lifecycle events only.
        </div>
      )}

      {/* The full record, in the same right-hand drawer Cluster access uses for a
          project — the rail and the row that raised the question stay on screen. */}
      {open && t && (
        <Drawer title="Device timeline" onClose={() => setOpen(false)}>
          <h2 style={{ marginTop: 0 }}>Timeline</h2>
          <p className="muted text-sm">
            Every recorded event for this device in the last {TIMELINE_WINDOW_DAYS} days, newest first.
          </p>
          <div className="stack-sm">
            {t.events.map((e, i) => <TimelineRow key={`${e.ts}-${i}`} e={e} />)}
          </div>
        </Drawer>
      )}
    </section>
  );
}

/** One activity line: coloured dot · <actor> did what · detail · when. */
function TimelineRow({ e }: { e: TimelineEvent }) {
  return (
    <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'flex-start' }}>
      <span
        aria-hidden="true"
        style={{
          flex: '0 0 auto',
          width: 7,
          height: 7,
          borderRadius: '50%',
          marginTop: 6,
          background: outcomeColor(e.outcome),
        }}
      />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="text-sm" style={{ overflowWrap: 'anywhere' }}>
          {e.actor && <strong>{e.actor}</strong>}
          {e.actor ? ' ' : ''}
          {e.title}
        </div>
        {e.detail && (
          <div className="muted text-2xs" style={{ overflowWrap: 'anywhere' }}>
            {e.detail}
          </div>
        )}
      </div>
      <span
        className="muted text-2xs"
        style={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}
        title={formatDateTime(e.ts)}
      >
        {timeAgo(e.ts)}
      </span>
    </div>
  );
}

/**
 * The rail shows the newest ten. A chatty device has 40+ reach rows, so a plain
 * slice would push "allocated" (the oldest event, and the one a stale peer is
 * read for) past the cut — it is pinned into the last slot instead.
 */
function previewEvents(events: TimelineEvent[] | undefined): TimelineEvent[] {
  if (!events) return [];
  if (events.length <= TIMELINE_PREVIEW_ROWS) return events;
  const head = events.slice(0, TIMELINE_PREVIEW_ROWS);
  // The allocation row is the one line every stale peer is read for; if the cut
  // dropped it, it takes the last slot (it is the oldest event, so order holds).
  const allocated = events.find((e) => e.kind === 'PEER_REGISTERED');
  if (allocated && !head.includes(allocated)) head[TIMELINE_PREVIEW_ROWS - 1] = allocated;
  return head;
}

interface TimelineEvent {
  ts: string;
  kind: string;
  actor?: string | null;
  title: string;
  detail?: string | null;
  outcome: string;
}

interface Timeline {
  auditAvailable: boolean;
  summary: {
    windowDays: number;
    attempts: number;
    established: number;
    successPercent?: number | null;
  };
  events: TimelineEvent[];
}

/** GraphQL serialises the enum KEY, so this sees OK / FAIL / NEUTRAL. */
function outcomeColor(outcome: string): string {
  const o = outcome.toLowerCase();
  if (o === 'ok') return 'var(--green)';
  if (o === 'fail') return 'var(--bad)';
  return 'var(--muted)';
}

/** Mirrors the thresholds the Peers page already uses for health badges. */
function reachBadgeClass(pct?: number | null): string {
  if (pct == null) return 'quiet';
  if (pct >= 90) return 'health-green';
  if (pct >= 60) return 'health-amber';
  return 'health-red';
}

/** never → red, quiet → amber, live → the existing quiet grey. */
export function handshakeBadgeClass(at?: string | null): string {
  const state = handshakeState(at);
  if (state === 'never') return 'health-red';
  if (state === 'quiet') return 'health-amber';
  return 'quiet';
}

/**
 * The colour of `securelinkStatus` is decided by whether the GATEWAY agrees,
 * not by the word itself.
 *
 * `connected` has two very different origins. mdm-api writes it at allocation
 * time for any device whose utils cannot report status —
 * `securelinkStatus: reportsStatus ? 'provisioned' : 'connected'` — which today
 * is the whole fleet (1498 of a 1500-device sample run 4.52). On a device where
 * the kernel has never seen a handshake, that word is an assumption the server
 * made ~27 ms after issuing the address, not an observation.
 *
 * Painting it green next to a red "Never connected" made the rail look broken.
 * It was not: the two badges are a claim and a measurement, and the claim is
 * wrong. Green is reserved for when the measurement backs the claim.
 */
export function statusBadgeClass(status: string, lastHandshakeAt?: string | null): string {
  if (status === 'disconnected') return 'health-red';
  if (status === 'connected') {
    // No handshake ever recorded ⇒ the server assumed this. Do not call it good.
    return handshakeState(lastHandshakeAt) === 'never' ? 'health-amber' : 'health-green';
  }
  return 'health-neutral';
}

export function statusBadgeTitle(
  status: string,
  updatedAt?: string | null,
  lastHandshakeAt?: string | null,
): string {
  const when = updatedAt ? ` (written ${formatDateTime(updatedAt)})` : '';
  if (status === 'connected' && handshakeState(lastHandshakeAt) === 'never') {
    return `Server-written at allocation because this device's utils cannot report status${when}. The gateway has never recorded a handshake, so this is not evidence of a connection.`;
  }
  if (status === 'connected') return `Device status${when}, and the gateway has seen a handshake.`;
  if (status === 'provisioned') return `Address issued; waiting for the device to confirm a handshake${when}.`;
  if (status === 'disconnected') return `The device reported its tunnel down${when}.`;
  return `Device status${when}.`;
}
